import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';
import type { Invoice, CreateInvoicePayload, SendInvoiceToCustomerPayload, CustomerSummary } from '../types';

interface ListInvoicesParams {
  tenantId?: string;
  status?: string;
  page?: number;
  limit?: number;
}

interface ListInvoicesResponse {
  data: Invoice[];
  total: number;
  page: number;
  limit: number;
}

export const invoicesApi = {
  list: async (params?: ListInvoicesParams): Promise<ListInvoicesResponse> => {
    const qs = params
      ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])).toString()
      : '';
    const response = await ApiFetch.get<Invoice[] | ListInvoicesResponse>(`/invoices${qs}`);
    if (Array.isArray(response)) {
      return {
        data: response,
        total: response.length,
        page: params?.page || 1,
        limit: params?.limit || 20
      };
    }
    return response;
  },

  get: async (id: string): Promise<Invoice> => {
    const response = await ApiFetch.get<Enveloped<Invoice>>(`/invoices/${id}`);
    return unwrap(response);
  },

  getOverdue: async (): Promise<Invoice[]> => {
    const response = await ApiFetch.get<Enveloped<Invoice[]>>('/invoices/overdue');
    return unwrap(response);
  },

  create: async (data: CreateInvoicePayload): Promise<Invoice> => {
    const response = await ApiFetch.post<Enveloped<Invoice>>('/invoices', data);
    return unwrap(response);
  },

  send: async (id: string): Promise<Invoice> => {
    const response = await ApiFetch.post<Enveloped<Invoice>>(`/invoices/${id}/send`);
    return unwrap(response);
  },

  markPaid: async (id: string): Promise<Invoice> => {
    const response = await ApiFetch.post<Enveloped<Invoice>>(`/invoices/${id}/mark-paid`);
    return unwrap(response);
  },

  void: async (id: string): Promise<Invoice> => {
    const response = await ApiFetch.post<Enveloped<Invoice>>(`/invoices/${id}/void`);
    return unwrap(response);
  },

  sendToCustomer: async (id: string, payload: SendInvoiceToCustomerPayload): Promise<{ messageId: string }> => {
    const response = await ApiFetch.post<Enveloped<{ messageId: string }>>(`/invoices/${id}/send-to-customer`, payload);
    return unwrap(response);
  },

  searchCustomers: async (merchantId: string, search: string): Promise<CustomerSummary[]> => {
    const qs = '?' + new URLSearchParams({ merchantId, ...(search ? { search } : {}) }).toString();
    const response = await ApiFetch.get<Enveloped<CustomerSummary[]> | CustomerSummary[]>(`/customers${qs}`);
    return Array.isArray(response) ? response : unwrap(response);
  },
};
